import React from "react";
import styled from "styled-components";
import {useMediaQuery} from "react-responsive";
import {CASE_STUDY_LIST} from "content/facts";

export default function CaseStudies() {
    const isTabletOrMobile = useMediaQuery({query: "(max-width: 1224px)"});
    const isShort = useMediaQuery({query: "(max-height: 820px)"});
    const listOnly = isShort && !isTabletOrMobile;

    return (
        <Root aria-labelledby="case-studies-heading">
            <Heading id="case-studies-heading">Case studies</Heading>
            {listOnly ? (
                <List>
                    {CASE_STUDY_LIST.map((study) => (
                        <Item key={study.title}>
                            <CardTitle>{study.title}</CardTitle>
                            <CardBody>{study.brief}</CardBody>
                        </Item>
                    ))}
                </List>
            ) : (
                <Grid $stack={isTabletOrMobile}>
                    {CASE_STUDY_LIST.map((study) => (
                        <Card key={study.title}>
                            <CardTitle>{study.title}</CardTitle>
                            <Points>
                                {study.points.map((point) => (
                                    <Point key={point.label}>
                                        <PointLabel>{point.label}</PointLabel>
                                        <PointText>{point.text}</PointText>
                                    </Point>
                                ))}
                            </Points>
                            <Flow aria-label={`${study.title} flow`}>
                                {study.flow.map((step, index) => (
                                    <React.Fragment key={step}>
                                        {index > 0 && (
                                            <Arrow aria-hidden="true">→</Arrow>
                                        )}
                                        <Step>{step}</Step>
                                    </React.Fragment>
                                ))}
                            </Flow>
                        </Card>
                    ))}
                </Grid>
            )}
        </Root>
    );
}

const Root = styled.section`
  min-width: 0;
  flex: 0 0 auto;
`;

const Heading = styled.h2`
  margin: 0 0 6px;
  font-size: 16px;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 6px;
  text-decoration-thickness: 4px;
  text-decoration-color: #8a8a8a;
`;

const Grid = styled.div<{ $stack: boolean }>`
  display: grid;
  grid-template-columns: ${(props) => (props.$stack ? "1fr" : "repeat(3, minmax(0, 1fr))")};
  gap: 10px;
`;

const Card = styled.article`
  border: 1px solid #8a8a8a;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const Item = styled.article`
  display: grid;
  grid-template-columns: minmax(6.5em, 7.5em) minmax(0, 1fr);
  gap: 8px;
  align-items: start;
  min-width: 0;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
`;

const CardBody = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.35;
  color: #e0e0e0;
`;

const Points = styled.dl`
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Point = styled.div`
  display: grid;
  grid-template-columns: 6.5em minmax(0, 1fr);
  gap: 8px;
  min-width: 0;
`;

const PointLabel = styled.dt`
  margin: 0;
  font-size: 11px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #d0d0d0;
`;

const PointText = styled.dd`
  margin: 0;
  font-size: 13px;
  line-height: 1.35;
  color: #e0e0e0;
`;

const Flow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
`;

const Step = styled.span`
  display: inline-flex;
  align-items: center;
  border: 1px solid #8a8a8a;
  padding: 3px 8px;
  font-size: 12px;
  line-height: 1.3;
`;

const Arrow = styled.span`
  color: #c8c8c8;
  font-size: 13px;
  line-height: 1;
`;
